/**
 * 浏览器生成图片的多种宽度版本（技术文档 4.2.4 方案 A）：
 * WebP 宽 640/1280/1920（质量约 0.8），原图窄于某档不生成、不放大；封面另生成宽 1280 的 JPEG 分享图（≤300KB）。
 * GIF 只留原图；生成失败（如浏览器不支持 WebP 编码）只传原图，官网退回原图显示。
 */

export const WEBP_WIDTHS = [640, 1280, 1920];
export const OG_IMAGE_WIDTH = 1280;
export const OG_IMAGE_MAX_BYTES = 300 * 1024;
const WEBP_QUALITY = 0.8;
const OG_QUALITY_STEPS = [0.85, 0.75, 0.65, 0.55, 0.45];

export interface ImageVariantFile {
    width: number;
    format: 'webp' | 'jpeg';
    blob: Blob;
}

export interface ImageSize {
    width: number;
    height: number;
}

const loadImage = (file: Blob): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
    };
    image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('image decode failed'));
    };
    image.src = url;
});

export const readImageSize = async (file: Blob): Promise<ImageSize> => {
    const image = await loadImage(file);
    return { width: image.naturalWidth, height: image.naturalHeight };
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> =>
    new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const drawScaled = (image: HTMLImageElement, width: number, background?: string): HTMLCanvasElement => {
    const height = Math.round((image.naturalHeight * width) / image.naturalWidth);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('canvas 2d context unavailable');
    if (background) {
        // JPEG 不支持透明，透明 PNG 铺白底，避免变黑
        context.fillStyle = background;
        context.fillRect(0, 0, width, height);
    }
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, width, height);
    return canvas;
};

/** 需要生成的 WebP 宽度：原图不小于该档才生成 */
export const plannedWebpWidths = (originalWidth: number): number[] =>
    WEBP_WIDTHS.filter((width) => originalWidth >= width);

/**
 * 生成各宽度版本；任何一步失败都返回已成功的部分（全部失败为空数组），不抛异常。
 * 浏览器不支持 WebP 编码时 toBlob 会退回 PNG，此时丢弃该版本。
 */
export const generateImageVariants = async (file: File, withOgImage: boolean): Promise<ImageVariantFile[]> => {
    if (file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif')) return [];
    let image: HTMLImageElement;
    try {
        image = await loadImage(file);
    } catch {
        return [];
    }
    const variants: ImageVariantFile[] = [];
    for (const width of plannedWebpWidths(image.naturalWidth)) {
        try {
            const blob = await canvasToBlob(drawScaled(image, width), 'image/webp', WEBP_QUALITY);
            if (blob && blob.type === 'image/webp') variants.push({ width, format: 'webp', blob });
        } catch {
            // 跳过该版本
        }
    }
    if (withOgImage && image.naturalWidth >= OG_IMAGE_WIDTH) {
        try {
            const canvas = drawScaled(image, OG_IMAGE_WIDTH, '#ffffff');
            for (const quality of OG_QUALITY_STEPS) {
                const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
                if (blob && blob.type === 'image/jpeg' && blob.size <= OG_IMAGE_MAX_BYTES) {
                    variants.push({ width: OG_IMAGE_WIDTH, format: 'jpeg', blob });
                    break;
                }
            }
        } catch {
            // 没有分享图时官网退回原图
        }
    }
    return variants;
};
